import { useMemo, useState } from "react";
import { useMgQual, useQuoteBank, type Quote } from "../lib/realdata";

/**
 * Quote Explorer — searchable, filterable view over the 123 real quotes
 * extracted from the 18 MG depth interviews. Filters: theme, city, respondent,
 * free-text search.
 */
export default function QuoteExplorer() {
  const bank = useQuoteBank();
  const qual = useMgQual();

  const [search, setSearch] = useState("");
  const [themeFilter, setThemeFilter] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState<string | null>(null);
  const [moderatorFilter, setModeratorFilter] = useState<string | null>(null);

  const themeList = useMemo(() => {
    if (!bank) return [] as string[];
    return Object.entries(bank.by_theme_count)
      .filter(([_, c]) => c > 0)
      .sort((a, b) => b[1] - a[1])
      .map(([t]) => t);
  }, [bank]);

  const cityList = useMemo(() => {
    if (!bank) return [] as string[];
    return Array.from(new Set(bank.quotes.map((q) => q.city))).sort();
  }, [bank]);

  const moderatorList = useMemo(() => {
    if (!bank) return [] as string[];
    return Array.from(new Set(bank.quotes.map((q) => q.moderator))).sort();
  }, [bank]);

  const filtered = useMemo(() => {
    if (!bank) return [] as Quote[];
    const s = search.trim().toLowerCase();
    return bank.quotes.filter((q) => {
      if (themeFilter && q.theme !== themeFilter) return false;
      if (cityFilter && q.city !== cityFilter) return false;
      if (moderatorFilter && q.moderator !== moderatorFilter) return false;
      if (s) {
        const hay = `${q.text} ${q.respondent} ${q.city} ${q.theme}`.toLowerCase();
        if (!hay.includes(s)) return false;
      }
      return true;
    });
  }, [bank, search, themeFilter, cityFilter, moderatorFilter]);

  const reset = () => {
    setSearch("");
    setThemeFilter(null);
    setCityFilter(null);
    setModeratorFilter(null);
  };

  const anyFilter = !!search.trim() || themeFilter || cityFilter || moderatorFilter;

  if (!bank || !qual) {
    return (
      <div className="stagger">
        <h1 className="h-page">Quote Explorer</h1>
        <div className="lead">Loading the quote bank…</div>
      </div>
    );
  }

  return (
    <div className="stagger">
      <div style={{ marginBottom: 16 }}>
        <h1 className="h-page">Quote Explorer</h1>
        <div className="lead">
          All {bank.total_quotes} respondent quotes extracted from the {qual.meta.total_interviews} depth interviews across {qual.meta.cities.length} cities.
          Every quote is tagged by theme, city, and moderator.
        </div>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr auto", gap: 10, alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search quote text, respondent name, theme…"
              style={{
                width: "100%", padding: "9px 12px 9px 34px",
                border: "1px solid var(--line)", borderRadius: "var(--r-2)",
                background: "var(--card)", outline: "none", fontSize: 13.5,
              }}
            />
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-40)" }}>
              <circle cx="11" cy="11" r="7" /><line x1="16" y1="16" x2="21" y2="21" />
            </svg>
          </div>

          <Select
            label="Theme"
            value={themeFilter}
            options={themeList}
            onChange={setThemeFilter}
          />
          <Select
            label="City"
            value={cityFilter}
            options={cityList}
            onChange={setCityFilter}
          />
          <Select
            label="Moderator"
            value={moderatorFilter}
            options={moderatorList}
            onChange={setModeratorFilter}
          />
          {anyFilter ? (
            <button className="btn-secondary" onClick={reset}>Clear filters</button>
          ) : (
            <div style={{ fontSize: 12, color: "var(--ink-40)", padding: "0 8px" }}>No filters</div>
          )}
        </div>
        <div style={{ marginTop: 12, fontSize: 12.5, color: "var(--ink-60)", display: "flex", gap: 16, flexWrap: "wrap" }}>
          <span>Showing <b className="num" style={{ color: "var(--ink)" }}>{filtered.length}</b> of {bank.total_quotes} quotes</span>
          {themeFilter && <span>Theme · <b style={{ color: "var(--ink)" }}>{themeFilter}</b></span>}
          {cityFilter && <span>City · <b style={{ color: "var(--ink)" }}>{cityFilter}</b></span>}
          {moderatorFilter && <span>Moderator · <b style={{ color: "var(--ink)" }}>{moderatorFilter}</b></span>}
        </div>
      </div>

      {/* Quick theme chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 18 }}>
        {themeList.map((t) => {
          const count = bank.by_theme_count[t] ?? 0;
          const active = themeFilter === t;
          return (
            <button
              key={t}
              onClick={() => setThemeFilter(active ? null : t)}
              style={{
                fontSize: 12, padding: "6px 10px",
                border: active ? "1px solid var(--teal)" : "1px solid var(--line)",
                background: active ? "var(--teal-tint)" : "var(--card)",
                color: active ? "var(--teal-ink)" : "var(--ink-2)",
                borderRadius: 999, cursor: "pointer",
                fontWeight: active ? 600 : 500,
                display: "inline-flex", alignItems: "center", gap: 6,
              }}
            >
              {t}
              <span className="num" style={{ fontSize: 10.5, opacity: 0.75 }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Quote grid */}
      {filtered.length === 0 ? (
        <div className="card" style={{ padding: 40, textAlign: "center", color: "var(--ink-60)", fontSize: 14 }}>
          No quotes match those filters.
          {anyFilter && <> Try <button className="btn-link" onClick={reset}>clearing filters</button>.</>}
        </div>
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
          gap: 14,
        }}>
          {filtered.map((q, i) => (
            <QuoteCard key={i} q={q} search={search} />
          ))}
        </div>
      )}
    </div>
  );
}

function Select({
  label, value, options, onChange,
}: {
  label: string;
  value: string | null;
  options: string[];
  onChange: (v: string | null) => void;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <span style={{ fontSize: 10.5, color: "var(--ink-40)", fontWeight: 500, letterSpacing: "0.04em", textTransform: "uppercase", marginBottom: 3 }}>{label}</span>
      <select
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        style={{
          padding: "8px 12px",
          border: "1px solid var(--line)",
          borderRadius: "var(--r-2)",
          background: "var(--card)",
          fontSize: 13, fontFamily: "var(--sans)",
          color: "var(--ink)",
          cursor: "pointer",
          outline: "none",
        }}
      >
        <option value="">All</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function QuoteCard({ q, search }: { q: Quote; search: string }) {
  const kindPalette: Record<string, string> = {
    "Jar cracking / breaking": "var(--coral)",
    "Motor burn-out / smell": "var(--coral)",
    "Loose / wobbly handle": "var(--amber)",
    "Noise at high speed": "var(--amber)",
    "Jar capacity": "var(--teal)",
    "Cleaning difficulty": "var(--amber)",
    "Price / value for money": "var(--ink-40)",
    "Service / after-sales": "var(--coral)",
    "Attachments / blades": "var(--teal)",
    "Durability / longevity": "var(--ok)",
    "Power / wattage": "var(--teal)",
    "Aesthetics / design": "var(--rose)",
    "Brand trust / reputation": "var(--ok)",
  };
  const colour = kindPalette[q.theme] ?? "var(--ink-40)";

  // Highlight search term in the text
  const body = useMemo(() => {
    if (!search.trim()) return q.text;
    try {
      const re = new RegExp(`(${search.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig");
      const parts = q.text.split(re);
      return parts.map((part, i) =>
        re.test(part) ? <mark key={i} style={{ background: "#FAF1DF", padding: "0 2px", borderRadius: 2 }}>{part}</mark> : <span key={i}>{part}</span>
      );
    } catch {
      return q.text;
    }
  }, [q.text, search]);

  return (
    <div style={{
      background: "var(--card)",
      border: "1px solid var(--line)",
      borderLeft: `3px solid ${colour}`,
      borderRadius: "var(--r-3)",
      padding: "16px 18px",
      display: "flex", flexDirection: "column", gap: 10,
      boxShadow: "var(--shadow-1)",
    }}>
      <div style={{
        fontSize: 10.5, fontWeight: 600, letterSpacing: "0.04em",
        textTransform: "uppercase", color: colour,
      }}>
        {q.theme}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.55, color: "var(--ink-2)", fontWeight: 400 }}>
        <span style={{ color: "var(--ink-40)", marginRight: 4 }}>"</span>
        {body}
        <span style={{ color: "var(--ink-40)", marginLeft: 2 }}>"</span>
      </div>
      <div style={{
        fontSize: 11, fontFamily: "var(--mono)",
        color: "var(--ink-60)", paddingTop: 8,
        borderTop: "1px solid var(--line-2)",
        display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 6,
      }}>
        <span>{q.respondent} · DI {q.di}</span>
        <span>{q.city} · {q.moderator}</span>
      </div>
    </div>
  );
}
